import { go, Model } from 'oderon-vue'
import TheUser from 'oderon-vue/auth/TheUser'
import { hasTag } from 'oderon-commons/src/utils'

import { Client } from './clients.js'
import { ServiceBI } from './service-bis.js'
import { Tracker } from './tracker.js'
import { Technician } from './technician.js'
import { ClientHistory } from './client-history.js'
import { BillingMeasureV2 } from './billing-measure-v2.js'
import { WifiNetwork, WifiAssoc } from './wifi.js'
import { Vehicle } from './vehicle.js'
import { CANDict } from './can.js'
import { Paperman } from './paperman.js'
import { Keyboard } from './keyboards.js'
import { CostCenter } from './cost-centers.js'
import { ProductType } from './product-types.js'

const Org = new Model('/organizations')
Org.findPrd = async function() {
  let rows = await Org.findAll()
  return rows.filter((o) => hasTag(o.extraInfo, 'PRD'))
}

Org.refreshToken = async function(id, rootPassword) {
  return await go.post(`/organizations/${id}/refresh-token`, { rootPassword })
}

const Heartbeat = new Model('/heartbeats', {
  resend: (id, ps) => go.post(`heartbeats/${id}/resend`, ps)
})

const MailboxItem = new Model('/mailbox')
MailboxItem.findLatest = async ({ clientId, information, onlyDelivered }) => {
  let ps = {
    limit: 1,
    order: 'createdAt desc, id desc',
    information,
    clientIds: clientId,
    skipDeleted: true,
    onlyDelivered
  }
  let { rows } = await go.get('/mailbox', ps)
  if (rows.length < 1) return null
  return go.get('/mailbox/' + rows[0].id)
}

MailboxItem.findLatestPair = async (clientId, information) => {
  let created = await MailboxItem.findLatest({ clientId, information })
  if (created == null) {
    return { created: null, delivered: null }
  }

  if (created.deliveredAt != null) {
    return { created, delivered: created }
  }

  let delivered = await MailboxItem.findLatest({
    clientId,
    information,
    onlyDelivered: true
  })

  return { created, delivered }
}

const SimCard = new Model('/sim-cards')
SimCard.findOperators = function() {
  return go('GET', '/sim-cards/operators')
}
SimCard.detectOperator = function(iccid) {
  return go('GET', '/sim-cards/operators/' + iccid)
}

const Depot = new Model('/depots')
const BrandModel = new Model('/brand-models')
const VehicleOwner = new Model('/vehicle-owners')

const Token = new Model('/tokens')

const Account = new Model('/accounts')
Account.updatePassword = function(id, newPassword) {
  return go.put(`/accounts/${id}/update-password`, { newPassword })
}
Account.findTagCloud = function() {
  return go.get('/accounts/tag-cloud')
}

const Subscription = new Model('/beeper/subscriptions')
Subscription.findByAccount = function(account) {
  return go('GET', `/beeper/subscriptions/account/${account}`)
}

const Queue = new Model('/queues')
Queue.flush = function(qname) {
  return go('POST', '/queues/flush', { qname })
}

Queue.findByClient = function(cid) {
  return go('GET', '/queues/client/' + cid)
}

Queue.peek = function(qname, { offset, limit }) {
  return go('GET', `/queues/peek/${qname}`, { offset, limit })
}

const AudioMessage = new Model('/audio-messages')
AudioMessage.getBlob = function(id) {
  return go.getBlob(`/audio-messages/${id}/contents`, { format: 'raw' })
}

const AudioSet = new Model('/audio-sets')

const Firmware = new Model('/firmwares')
Firmware.getBlob = function(id) {
  return go.getBlob(`/firmwares/${id}/download`, { format: 'raw' })
}

const FWUpdate = new Model('/firmware-updates')

const PipeDiags = new Model('/pipe-diags', {
  findByClient: (cid) => go('GET', `/pipe-diags/client/${cid}`),
  findFullByClient: (cid) => go('GET', `/pipe-diags/client/${cid}/full`)
})

PipeDiags.restartWorker = function({ orgCode, clientId, extraInfo }) {
  return go('POST', `/pipe-diags/restart`, { orgCode, clientId, extraInfo })
}

const SysConfig = {
  findAll() {
    return go('GET', `/configs`)
  },
  save(obj) {
    return go('POST', `/configs`, obj)
  }
}

const Redis = {
  findAll(db, ps) {
    return go('GET', `/redis-browser/${db}`, ps)
  },
  findOne(db, key) {
    return go('GET', `/redis-browser/${db}/${key}`)
  },
  delete(db, key) {
    return go('DELETE', `/redis-browser/${db}/${key}`)
  }
}

const RawdataEvent = {
  findAll(ps) {
    return go('GET', `/rawdata-events`, ps)
  },
  findById(id) {
    return go('GET', `/rawdata-events/${id}`)
  },
  findTypes() {
    return go('GET', `/rawdata-events/types`)
  }
}

const ClientDiags = {
  findAll(params) {
    return go('GET', `/client-diags`, params)
  }
}

// const Vehicle = {
//   findAll(params) {
//     return go('GET', `/vehicles`, params)
//   },
//   findById(id, params) {
//     return go('GET', `/vehicles/${id}`, params)
//   },
//   download(ps) {
//     return go('GET', 'vehicles/download', ps, { asBlob: true })
//   }
// }

class OrgApi {
  constructor(orgIdOrCode) {
    if (orgIdOrCode.name) orgIdOrCode = orgIdOrCode.name
    this.orgIdOrCode = orgIdOrCode
  }

  go(method, path, data) {
    if (path.indexOf('//') == 0) path = '/' + path.substring(2)

    return go(method, `/organizations/${this.orgIdOrCode}/api/v1/${path}`, data)
  }

  get(path, qs) {
    return this.go('GET', path, qs)
  }
  post(path, qs) {
    return this.go('POST', path, qs)
  }
  put(path, qs) {
    return this.go('PUT', path, qs)
  }
  delete(path, qs) {
    return this.go('DELETE', path, qs)
  }
  patch(path, qs) {
    return this.go('PATCH', path, qs)
  }
}

function downloadify(path, params) {
  let strParams = go.toQueryString({
    ...params,
    access_token: TheUser.getToken()
  })

  let base = go.getDefaults().base

  return go.join(base, path) + '?' + strParams
}

const Customer = new Model('/customers', {
  findClassifications: function() {
    return go.get('/customers/classifications')
  },
  findMarketSegments: function() {
    return go.get('/customers/market-segments')
  },
  associateAccount: (customerId, accountId) => {
    return go.post(`/customers/${customerId}/accounts`, { accountId })
  },

  dissociateAccount: function(customerId, accountId) {
    if (accountId == null || customerId == null)
      throw new Error('Missing customerId/accountId')
    return go.delete(`/customers/${customerId}/accounts/${accountId}`)
  },

  associateOrg: function({ customerId, orgId, ...body }) {
    return go.put(`/customers/${customerId}/orgs/${orgId}`, body)
  },

  dissociateOrg: function({ customerId, orgId }) {
    if (orgId == null || customerId == null)
      throw new Error('Missing customerId/orgId')
    return go.delete(`/customers/${customerId}/orgs/${orgId}`)
  },

  associateCostCenter: function({ customerId, costCenterId, ...body }) {
    return go.put(`/customers/${customerId}/cost-centers/${costCenterId}`, body)
  },

  dissociateCostCenter: function({ customerId, costCenterId }) {
    if (costCenterId == null || customerId == null)
      throw new Error('Missing customerId/costCenterId')
    return go.delete(`/customers/${customerId}/cost-centers/${costCenterId}`)
  },

  reactivate: function(customerId) {
    if (customerId == null) throw new Error('Missing customerId')
    return go.post(`/customers/${customerId}/reactivate`)
  }

  // findHealth: function(customerId) {
  //   return go.get(`/customers/${customerId}/health`)
  // },

  // findInstalledBase: function(customerId) {
  //   return go.get(`/customers/${customerId}/installed-base`)
  // }
})

const InstalledBase = {
  findById: (id) => go.get('/installed-base/' + id),
  findPrimaryByCid: (id) => go.get(`/installed-base/${id}:primary`),
  findAll: (ps) => go.get('/installed-base', ps),
  getGrid: (ps) => go.get('/installed-base/grid', ps),
  getGridSpec: (ps) => go.get('/installed-base/grid/spec', ps),
  downloadGrid: (ps) => go.download('/installed-base/grid/download', ps),
  getStats: (ps) => go.get('/installed-base/stats', ps)
}

const ScreenSet = new Model('/screen-sets')
ScreenSet.previewButton = (body) => go.post('/screen-sets/preview/button', body)
ScreenSet.previewScreen = (body) => go.post('/screen-sets/preview/screen', body)
ScreenSet.previewScreenSet = (body) =>
  go.post('/screen-sets/preview/screen-set', body)
ScreenSet.sync = (body) => go.post('/screen-sets/sync', body)

const Task = new Model('/tasks')
Task.patch = (id, body) => go.patch(`/tasks/${id}`, body)
Task.findImages = (id) => go.get(`/tasks/${id}/images`)
Task.addImage = (id, blob) => {
  let body = new FormData()
  body.append('file', blob)
  body.append('mimeType', blob.type)
  return go.post(`/tasks/${id}/images`, body)
}
Task.removeImage = (id, imageId) => go.delete(`/tasks/${id}/images/${imageId}`)
Task.getImageBlob = (id, imageId) =>
  go.download(`/tasks/${id}/images/${imageId}`, { asBlob: true })

async function minMs(ms = 0, prom) {
  if (typeof prom === 'function') prom = prom()

  let [res0] = await Promise.all([
    prom,
    new Promise((resolve) => setTimeout(resolve, ms))
  ])
  return res0
}

const minMs1 = (prom) => minMs(1000, prom)
const minMs3 = (prom) => minMs(300, prom)
const minMs5 = (prom) => minMs(500, prom)

export {
  go,
  downloadify,
  Model,
  Org,
  Client,
  Heartbeat,
  MailboxItem,
  OrgApi,
  Account,
  Subscription,
  Queue,
  PipeDiags,
  AudioMessage,
  Firmware,
  FWUpdate,
  AudioSet,
  SysConfig,
  ClientDiags,
  SimCard,
  Depot,
  Technician,
  ClientHistory,
  BrandModel,
  CostCenter,
  ServiceBI,
  Redis,
  Token,
  RawdataEvent,
  VehicleOwner,
  Customer,
  InstalledBase,
  ScreenSet,
  Task,
  Tracker,
  minMs,
  minMs1,
  minMs3,
  minMs5,
  BillingMeasureV2,
  CANDict,
  WifiNetwork,
  WifiAssoc,
  Vehicle,
  Paperman,
  Keyboard,
  ProductType
}
